import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import AdminLayout, { type NavItem } from '../../components/admin/AdminLayout';
import SessionBookingsModal from '../../components/admin/SessionBookingsModal';
import SessionWaitlistModal from '../../components/admin/SessionWaitlistModal';
import { TOUR, tourAttr } from '../../features/hostTour';
import { useAuth } from '../../hooks/useAuth';
import { bilingualLines, localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';

const hostNavItems: NavItem[] = [
  { to: '/host/activities', label: 'Activities' },
  { to: '/host/courses', label: 'Courses' },
  { to: '/host/passes', label: 'Passes' },
  { to: '/host/bookings', label: 'Bookings' },
  { to: '/host/email', label: 'Email' },
];

interface ActivitySession {
  id: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  currentBookings: number;
  remainingSpots: number;
  hasBookingHistory?: boolean;
  waitlistCount?: number;
  seriesId?: string | null;
}

interface Activity {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  slug: string;
  status: string;
  category: string;
  region: string[];
  price: number;
  adultPrice?: number;
  childRequired?: boolean;
  ageMin: number;
  ageMax: number;
  sessionsCount: number;
  totalBookings: number;
  sessions: ActivitySession[];
  images: Array<{ imageUrl: string; isCover: boolean }>;
  createdAt: string;
}

const REGION_LABELS: Record<string, string> = {
  SOUTH_BAY: 'South Bay',
  PENINSULA: 'Peninsula',
  EAST_BAY: 'East Bay',
  SAN_FRANCISCO: 'San Francisco',
  OTHER: 'Other',
};

export default function AdminActivities() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();

  const isHostContext = location.pathname.startsWith('/host/');
  const apiPrefix = isHostContext ? 'host' : 'admin';
  const basePath = isHostContext ? '/host/activities' : '/admin/activities';

  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'active' | 'past' | 'draft'>('active');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

        const response = await fetch(`${apiUrl}/${apiPrefix}/activities`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch activities');
        }

        const result = await response.json();
        if (result.success) {
          setActivities(result.data || []);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load activities';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [isAuthenticated, getToken, apiPrefix]);

  const now = new Date();
  const hasFutureSessions = (a: Activity) => a.sessions?.some((s) => new Date(s.endTime) >= now);
  const filteredActivities = activities.filter((activity) => {
    if (filter === 'draft') return activity.status === 'DRAFT';
    if (filter === 'active') return activity.status === 'APPROVED' && hasFutureSessions(activity);
    // Past tab: APPROVED with all sessions expired, or legacy INACTIVE activities
    if (filter === 'past')
      return (
        (activity.status === 'APPROVED' && !hasFutureSessions(activity)) ||
        activity.status === 'INACTIVE'
      );
    return false;
  });

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this activity?')) return;

    try {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

      const response = await fetch(`${apiUrl}/${apiPrefix}/activities/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete activity');
      }

      setActivities(activities.filter((a) => a.id !== id));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete activity';
      setError(errorMessage);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading activities...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout
      title="Activity Management"
      navItems={isHostContext ? hostNavItems : undefined}
      headerRight={
        <Link
          to={`${basePath}/new`}
          className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          {...tourAttr(TOUR.activitiesCreate)}
        >
          <span className="mr-2">+</span>
          Create Activity
        </Link>
      }
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div>
          <nav className="flex -mb-px overflow-x-auto" {...tourAttr(TOUR.activitiesTabs)}>
            {(['active', 'past', 'draft'] as const).map((tab) => {
              const approved = activities.filter((a) => a.status === 'APPROVED');
              const inactive = activities.filter((a) => a.status === 'INACTIVE');
              const count =
                tab === 'draft'
                  ? activities.filter((a) => a.status === 'DRAFT').length
                  : tab === 'active'
                    ? approved.filter((a) => hasFutureSessions(a)).length
                    : approved.filter((a) => !hasFutureSessions(a)).length + inactive.length;

              return (
                <button
                  key={tab}
                  onClick={() => setFilter(tab)}
                  data-tour={
                    tab === 'draft'
                      ? TOUR.activitiesTabDraft
                      : tab === 'active'
                        ? TOUR.activitiesTabActive
                        : TOUR.activitiesTabPast
                  }
                  className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap flex-shrink-0 ${
                    filter === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">{count}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Activities Grid */}
      {filteredActivities.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="text-gray-400 text-5xl mb-4">📅</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No activities yet</h3>
          <p className="text-gray-500 mb-6">Create your first activity to get started.</p>
          <Link
            to={`${basePath}/new`}
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Create Activity
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredActivities.map((activity) => (
            <ActivityCard
              key={activity.id}
              activity={activity}
              onEdit={() => navigate(`${basePath}/${activity.id}/edit`)}
              onDelete={() => handleDelete(activity.id)}
              getToken={getToken}
              isHostContext={isHostContext}
            />
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

function ActivityCard({
  activity,
  onEdit,
  onDelete,
  getToken,
  isHostContext,
}: {
  activity: Activity;
  onEdit: () => void;
  onDelete: () => void;
  getToken: () => Promise<string | undefined>;
  isHostContext?: boolean;
}) {
  const siteLanguage = useSiteLanguage();
  // Two-line title, the user's language on top (PRD §5.2).
  const [titleTop, titleBottom] = bilingualLines(activity.titleCN, activity.titleEN, siteLanguage);
  const [viewingSession, setViewingSession] = useState<{
    id: string;
    date: string;
  } | null>(null);
  const [viewingWaitlist, setViewingWaitlist] = useState<{
    id: string;
    date: string;
  } | null>(null);
  const coverImage = activity.images?.find((img) => img.isCover)?.imageUrl;

  const handleViewSession = (sessionId: string, sessionDate: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setViewingSession({ id: sessionId, date: sessionDate });
  };

  const handleManageWaitlist = (sessionId: string, sessionDate: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setViewingWaitlist({ id: sessionId, date: sessionDate });
  };

  const formatSessionDateTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const date = start.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    const formatTime = (d: Date) => {
      const h = d.getHours();
      const m = d.getMinutes();
      const period = h >= 12 ? 'pm' : 'am';
      const hour = h % 12 || 12;
      return m === 0 ? `${hour}${period}` : `${hour}:${m.toString().padStart(2, '0')}${period}`;
    };
    return `${date} ${formatTime(start)}-${formatTime(end)}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden" {...tourAttr(TOUR.activityCard)}>
      {/* Image */}
      <div className="h-40 bg-gray-200 relative">
        {coverImage ? (
          <img
            src={coverImage}
            alt={localize(activity.titleCN, activity.titleEN, siteLanguage)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            No image
          </div>
        )}
        <div className="absolute top-2 right-2">
          <StatusBadge status={activity.status} />
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">{titleTop}</h3>
        {titleBottom && <p className="text-sm text-gray-500 mb-2 line-clamp-1">{titleBottom}</p>}

        <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
          <span>
            {activity.childRequired === false
              ? `$${((activity.adultPrice ?? 0) / 100).toFixed(0)}/adult`
              : `$${(activity.price / 100).toFixed(0)}/child`}
          </span>
          <span>{activity.sessionsCount} sessions</span>
          <span>{activity.totalBookings} bookings</span>
        </div>

        <div className="mb-3 flex flex-wrap gap-1">
          {activity.region.length > 0 ? (
            activity.region.map((r) => (
              <span
                key={r}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-pale text-primary"
              >
                {REGION_LABELS[r] ?? r}
              </span>
            ))
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
              ⚠ No region
            </span>
          )}
        </div>

        {/* Session Details */}
        {activity.sessions && activity.sessions.length > 0 && (
          <SessionList
            sessions={activity.sessions}
            formatSessionDateTime={formatSessionDateTime}
            onViewSession={handleViewSession}
            onManageWaitlist={handleManageWaitlist}
            isHostContext={!!isHostContext}
          />
        )}

        {/* Actions */}
        <div className="flex items-center gap-2" {...tourAttr(TOUR.activityCardActions)}>
          <button
            onClick={onEdit}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Edit
          </button>
          {(!isHostContext || activity.status === 'APPROVED') && (
            <Link
              to={`/activity/${activity.slug}`}
              target="_blank"
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              View
            </Link>
          )}
          <button
            onClick={onDelete}
            className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Session Bookings Modal */}
      {viewingSession && (
        <SessionBookingsModal
          sessionId={viewingSession.id}
          activityTitle={localize(activity.titleCN, activity.titleEN, siteLanguage)}
          sessionDate={viewingSession.date}
          getToken={getToken}
          onClose={() => setViewingSession(null)}
          apiPrefix={isHostContext ? 'host' : 'admin'}
        />
      )}

      {/* Session Waitlist Modal (admin only) */}
      {viewingWaitlist && !isHostContext && (
        <SessionWaitlistModal
          sessionId={viewingWaitlist.id}
          activityTitle={localize(activity.titleCN, activity.titleEN, siteLanguage)}
          sessionDate={viewingWaitlist.date}
          getToken={getToken}
          onClose={() => setViewingWaitlist(null)}
        />
      )}
    </div>
  );
}

function SessionRow({
  session,
  formatSessionDateTime,
  onViewSession,
  onManageWaitlist,
  isPast,
  isHostContext,
}: {
  session: ActivitySession;
  formatSessionDateTime: (start: string, end: string) => string;
  onViewSession: (id: string, date: string, e: React.MouseEvent) => void;
  onManageWaitlist: (id: string, date: string, e: React.MouseEvent) => void;
  isPast: boolean;
  isHostContext: boolean;
}) {
  const isFull = session.currentBookings >= session.maxCapacity;
  const waitlistCount = session.waitlistCount ?? 0;
  return (
    <div
      className={`text-xs px-2 py-1 rounded flex items-center justify-between ${
        isPast ? 'bg-gray-50 text-gray-400' : 'bg-gray-50 text-gray-700'
      }`}
    >
      <span className="flex items-center gap-1.5">
        {formatSessionDateTime(session.startTime, session.endTime)}
        {session.seriesId && (
          <span
            title="Part of a repeating series"
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700"
          >
            <span aria-hidden>🔁</span>Series
          </span>
        )}
      </span>
      <div className="flex items-center gap-2">
        <span className={isPast ? 'text-gray-400' : 'text-gray-600'}>
          {session.currentBookings}/{session.maxCapacity} booked
        </span>
        {!isPast && waitlistCount > 0 && (
          <span className="text-gray-600">{waitlistCount} waitlist</span>
        )}
        {(session.currentBookings > 0 || session.hasBookingHistory) && (
          <button
            onClick={(e) =>
              onViewSession(
                session.id,
                formatSessionDateTime(session.startTime, session.endTime),
                e
              )
            }
            className="text-blue-600 hover:underline"
          >
            View
          </button>
        )}
        {!isHostContext && !isPast && (isFull || waitlistCount > 0) && (
          <button
            onClick={(e) =>
              onManageWaitlist(
                session.id,
                formatSessionDateTime(session.startTime, session.endTime),
                e
              )
            }
            className="text-blue-600 hover:underline"
          >
            Manage
          </button>
        )}
      </div>
    </div>
  );
}

function SessionList({
  sessions,
  formatSessionDateTime,
  onViewSession,
  onManageWaitlist,
  isHostContext,
}: {
  sessions: ActivitySession[];
  formatSessionDateTime: (start: string, end: string) => string;
  onViewSession: (id: string, date: string, e: React.MouseEvent) => void;
  onManageWaitlist: (id: string, date: string, e: React.MouseEvent) => void;
  isHostContext: boolean;
}) {
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const now = new Date();
  const upcoming = sessions.filter((s) => new Date(s.endTime) >= now);
  const past = sessions.filter((s) => new Date(s.endTime) < now);

  const pastTotalBooked = past.reduce((sum, s) => sum + s.currentBookings, 0);
  const pastTotalCapacity = past.reduce((sum, s) => sum + s.maxCapacity, 0);

  return (
    <div className="mb-4" {...tourAttr(TOUR.activityCardSessions)}>
      {/* Upcoming sessions */}
      {upcoming.length > 0 && (
        <div>
          <button
            onClick={() => setShowUpcoming(!showUpcoming)}
            className="w-full flex items-center justify-between text-sm text-gray-700 hover:text-gray-900 py-2"
          >
            <span className="font-medium">{upcoming.length} upcoming</span>
            <svg
              className={`w-4 h-4 transition-transform ${showUpcoming ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {showUpcoming && (
            <div className="space-y-1 pb-2">
              {upcoming.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  formatSessionDateTime={formatSessionDateTime}
                  onViewSession={onViewSession}
                  onManageWaitlist={onManageWaitlist}
                  isPast={false}
                  isHostContext={isHostContext}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Past sessions - collapsed summary */}
      {past.length > 0 && (
        <div>
          <button
            onClick={() => setShowPast(!showPast)}
            className="w-full flex items-center justify-between text-sm text-gray-400 hover:text-gray-600 py-2"
          >
            <span>
              {past.length} past · {pastTotalBooked}/{pastTotalCapacity} booked
            </span>
            <svg
              className={`w-3 h-3 transition-transform ${showPast ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {showPast && (
            <div className="space-y-1 pb-2">
              {past.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  formatSessionDateTime={formatSessionDateTime}
                  onViewSession={onViewSession}
                  onManageWaitlist={onManageWaitlist}
                  isPast={true}
                  isHostContext={isHostContext}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    APPROVED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
    DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
    PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
    REJECTED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
    INACTIVE: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Archived' },
  };

  const config = statusConfig[status] || statusConfig.DRAFT;

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}
